import { BadgeCheck } from 'lucide-react';
import Avatar from '../ui/Avatar';
import StarRating from '../ui/StarRating';
import { formatCount, formatPrice } from '../../lib/format';
import { peerFact } from '../../lib/peerFacts';

const SUCCESSFUL = new Set(['COMPLETED', 'DELIVERED']);

/**
 * Stats derived from the real order history between the two accounts, so
 * "Successful + Refund = Past Orders" always holds and the numbers can never
 * disagree with the list on the My Orders tab.
 *
 * The API returns every order between the pair, so these are true totals rather
 * than a page of a longer list.
 */
function deriveStats(orders) {
  return {
    pastOrders: orders.length,
    totalSpending: orders.reduce((sum, order) => sum + (Number(order.price) || 0), 0),
    successfulOrders: orders.filter((order) => SUCCESSFUL.has(order.status)).length,
    refundOrders: orders.filter((order) => order.status === 'REFUNDED').length,
  };
}

function StatsList({ orders, loading }) {
  const stats = deriveStats(orders);

  const rows = [
    { key: 'pastOrders', label: 'Past Orders', value: formatCount(stats.pastOrders) },
    { key: 'totalSpending', label: 'Total Spending', value: formatPrice(stats.totalSpending) },
    {
      key: 'successfulOrders',
      label: 'Successful Orders',
      value: formatCount(stats.successfulOrders),
    },
    { key: 'refundOrders', label: 'Refund Orders', value: formatCount(stats.refundOrders) },
  ];

  return (
    <section
      aria-label="Order statistics"
      className="rounded-2xl border border-line bg-surface px-4 shadow-soft"
    >
      <ul className="divide-y divide-line">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center justify-between gap-3 py-2.5">
            <span className="text-[12.5px] text-ink-2">{row.label}</span>
            <span className="text-[12.5px] font-semibold text-ink tabular-nums">
              {loading ? '-' : row.value}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * "User Profile" tab. Rating comes from the public profile lookup, which may be
 * unavailable (seeded accounts have no username) - it degrades to "No ratings yet"
 * rather than inventing a number.
 *
 * A SELLER's profile is deliberately reduced for the buyer looking at it:
 * order counts and spending are business/private signals, so only the public
 * identity (avatar, name, rating, username) is shown. A buyer's profile
 * (what a seller sees) keeps the full card, because those numbers are the
 * seller's own sales figures with that buyer.
 *
 * The COUNTRY row is gone entirely (owner request: no country anywhere in chat).
 * Its cell is replaced by the counterparty's public identity fact - seller rank,
 * else how long they have been a member - and is dropped rather than blanked
 * when neither exists. See lib/peerFacts.js.
 */
export default function UserProfilePanel({ conversation, profile, orders, ordersLoading }) {
  const { name, username, avatarUrl, online, verified, partnerRole } = conversation;

  const rating = Number(profile?.rating ?? 0);
  const totalRatings = Number(profile?.totalRatings ?? 0);
  const hasRating = totalRatings > 0 && rating > 0;

  // Seller → hide the private side (stats).
  const showPrivateStats = partnerRole !== 'SELLER';

  const fact = peerFact(profile);

  return (
    <div className="space-y-3">
      <section
        aria-label="User profile"
        className="rounded-2xl border border-line bg-surface p-4 shadow-soft"
      >
        <div className="flex items-center gap-3">
          <Avatar name={name} src={avatarUrl} online={online} size="lg" />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[14px] font-semibold text-ink">{name}</span>
              {verified && (
                <BadgeCheck size={14} aria-label="Verified seller" className="shrink-0 text-accent" />
              )}
            </div>

            <div className="mt-0.5 flex items-center gap-1.5">
              {hasRating ? (
                <>
                  <StarRating value={rating} />
                  <span className="text-[11px] text-ink-3 tabular-nums">{rating.toFixed(1)}</span>
                </>
              ) : (
                <span className="text-[11px] text-ink-3">No ratings yet</span>
              )}
            </div>

            <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-ink-3">
              <span
                aria-hidden="true"
                className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-online' : 'bg-ink-3'}`}
              />
              {online ? 'Online' : 'Offline'}
            </p>
          </div>
        </div>

        <dl
          className={`mt-3 grid gap-x-3 gap-y-1.5 border-t border-line pt-3 ${
            fact ? 'grid-cols-2' : 'grid-cols-1'
          }`}
        >
          <div>
            <dt className="text-[10.5px] tracking-wide text-ink-3 uppercase">Username</dt>
            <dd className="truncate text-[11.5px] font-medium text-ink">
              {username ? `@${username}` : '-'}
            </dd>
          </div>
          {fact && (
            <div>
              <dt className="text-[10.5px] tracking-wide text-ink-3 uppercase">{fact.label}</dt>
              <dd className="truncate text-[11.5px] font-medium text-ink">{fact.value}</dd>
            </div>
          )}
        </dl>
      </section>

      {showPrivateStats && <StatsList orders={orders} loading={ordersLoading} />}
    </div>
  );
}
